(function() {
  'use strict';

  var UpsellRecommendations = (function() {
    var instance = null;
    var abortController = null;
    var debounceTimers = {};
    var boundHandlers = {};
    var _loadPromise = null;

    var state = {
      cartItems: [],
      products: [],
      productsCache: null,
      isLoading: false,
      initialized: false,
      visible: false
    };

    var CACHE_KEY_FP = 'upsell_cart_fp';
    var CACHE_KEY_PRODUCTS = 'upsell_products';

    var lastCartFingerprint = '';
    var domCache = {};
    var dragState = { isDragging: false, startX: 0, scrollLeft: 0, moved: false };
    var productDataMap = new Map();

    var CONFIG = {
      fallbackCollectionHandle: 'best-seller-1',
      upsellCollectionHandle: '',
      productsLimit: 4,
      debounceMs: 150,
      apiTimeoutMs: 5000,
      imageHeight: 100
    };

    function getInstance() {
      if (!instance) {
        instance = createInstance();
      }
      return instance;
    }

    function createInstance() {
      return {
        init: init,
        load: load,
        refresh: refresh,
        destroy: destroy,
        getState: function() { return Object.assign({}, state); },
        prefetch: prefetch
      };
    }

    function init() {
      if (state.initialized && domCache.container) {
        return;
      }

      if (state.initialized) {
        unbindEvents();
      }

      domCache.container = document.getElementById('UpsellRecommendations');
      domCache.slider = document.getElementById('UpsellSlider');
      domCache.productTemplate = document.getElementById('UpsellProductTemplate');
      domCache.fallbackContainer = document.getElementById('UpsellFallback');

      if (!domCache.container || !domCache.slider) {
        return;
      }

      mergeConfig();
      state.initialized = true;
      bindEvents();

      if (state.productsCache && state.productsCache.length > 0) {
        render();
      }

      if ('requestIdleCallback' in window) {
        requestIdleCallback(function() {
          prefetchData();
        }, { timeout: 2000 });
      } else {
        setTimeout(function() {
          prefetchData();
        }, 500);
      }
    }

    function readSessionCache() {
      try {
        var fp = sessionStorage.getItem(CACHE_KEY_FP);
        var data = sessionStorage.getItem(CACHE_KEY_PRODUCTS);
        if (fp && data) {
          return { fingerprint: fp, products: JSON.parse(data) };
        }
      } catch (e) {}
      return null;
    }

    function writeSessionCache(fp, products) {
      try {
        sessionStorage.setItem(CACHE_KEY_FP, fp);
        sessionStorage.setItem(CACHE_KEY_PRODUCTS, JSON.stringify(products));
      } catch (e) {}
    }

    function clearSessionCache() {
      try {
        sessionStorage.removeItem(CACHE_KEY_FP);
        sessionStorage.removeItem(CACHE_KEY_PRODUCTS);
      } catch (e) {}
    }

    function sharedLoad() {
      if (_loadPromise) return _loadPromise;

      _loadPromise = fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(res) { return res.json(); })
        .then(function(cart) {
          state.cartItems = cart.items || [];
          var fp = cartFingerprint(state.cartItems);
          lastCartFingerprint = fp;

          var cached = readSessionCache();
          if (cached && cached.fingerprint === fp && cached.products && cached.products.length > 0) {
            state.productsCache = cached.products;
            return cached.products;
          }

          return fetchProducts().then(function(products) {
            if (products && products.length > 0) {
              writeSessionCache(fp, products);
            }
            return products;
          });
        })
        .then(function(products) {
          _loadPromise = null;
          return products;
        })
        .catch(function(e) {
          _loadPromise = null;
          throw e;
        });

      return _loadPromise;
    }

    function prefetchData() {
      if (state.productsCache && state.productsCache.length > 0) {
        return;
      }

      sharedLoad().then(function(products) {
        if (products && products.length > 0) {
          state.products = products;
          state.productsCache = products;
          render();
        }
      }).catch(function() {});
    }

    function mergeConfig() {
      if (window.upsellConfig) {
        CONFIG.fallbackCollectionHandle = window.upsellConfig.fallbackCollectionHandle || CONFIG.fallbackCollectionHandle;
        CONFIG.upsellCollectionHandle = window.upsellConfig.upsellCollectionHandle || CONFIG.upsellCollectionHandle;
        CONFIG.productsLimit = window.upsellConfig.productsLimit || CONFIG.productsLimit;
      }
    }

    function bindEvents() {
      boundHandlers.click = handleContainerClick;
      boundHandlers.change = handleContainerChange;
      boundHandlers.dragStart = handleDragStart;
      boundHandlers.dragMove = handleDragMove;
      boundHandlers.dragEnd = handleDragEnd;
      boundHandlers.touchStart = handleTouchStart;
      boundHandlers.touchMove = handleTouchMove;
      boundHandlers.touchEnd = handleTouchEnd;
      boundHandlers.cartOpen = handleCartOpen;
      boundHandlers.cartRendered = handleCartRendered;
      boundHandlers.cartUpdated = handleCartUpdated;

      domCache.container.addEventListener('click', boundHandlers.click);
      domCache.container.addEventListener('change', boundHandlers.change);

      domCache.slider.addEventListener('mousedown', boundHandlers.dragStart);
      domCache.slider.addEventListener('mousemove', boundHandlers.dragMove);
      domCache.slider.addEventListener('mouseup', boundHandlers.dragEnd);
      domCache.slider.addEventListener('mouseleave', boundHandlers.dragEnd);
      domCache.slider.addEventListener('touchstart', boundHandlers.touchStart, { passive: true });
      domCache.slider.addEventListener('touchmove', boundHandlers.touchMove, { passive: true });
      domCache.slider.addEventListener('touchend', boundHandlers.touchEnd);

      document.addEventListener('cart:open', boundHandlers.cartOpen);
      document.addEventListener('drawerOpen.CartDrawer', boundHandlers.cartOpen);
      document.addEventListener('cart:rendered', boundHandlers.cartRendered);
      document.addEventListener('cart:updated', boundHandlers.cartUpdated);
    }

    function unbindEvents() {
      if (!domCache.container) return;

      domCache.container.removeEventListener('click', boundHandlers.click);
      domCache.container.removeEventListener('change', boundHandlers.change);

      if (domCache.slider) {
        domCache.slider.removeEventListener('mousedown', boundHandlers.dragStart);
        domCache.slider.removeEventListener('mousemove', boundHandlers.dragMove);
        domCache.slider.removeEventListener('mouseup', boundHandlers.dragEnd);
        domCache.slider.removeEventListener('mouseleave', boundHandlers.dragEnd);
        domCache.slider.removeEventListener('touchstart', boundHandlers.touchStart);
        domCache.slider.removeEventListener('touchmove', boundHandlers.touchMove);
        domCache.slider.removeEventListener('touchend', boundHandlers.touchEnd);
        domCache.slider.removeEventListener('scroll', handleScroll);
      }

      document.removeEventListener('cart:open', boundHandlers.cartOpen);
      document.removeEventListener('drawerOpen.CartDrawer', boundHandlers.cartOpen);
      document.removeEventListener('cart:rendered', boundHandlers.cartRendered);
      document.removeEventListener('cart:updated', boundHandlers.cartUpdated);
    }

    function handleContainerClick(e) {
      if (dragState.moved) {
        e.preventDefault();
        return;
      }

      var addBtn = e.target.closest('.upsell-product__add-btn');
      if (addBtn && !addBtn.disabled) {
        e.preventDefault();
        handleAddToCart(addBtn);
        return;
      }

      var dot = e.target.closest('.upsell-recommendations__dot');
      if (dot) {
        e.preventDefault();
        var index = parseInt(dot.dataset.index, 10);
        scrollToProduct(index);
        return;
      }
    }

    function handleContainerChange(e) {
      if (e.target.classList.contains('upsell-product__variant-select')) {
        handleVariantChange(e.target);
      }
    }

    function debounce(key, fn, delay) {
      if (debounceTimers[key]) {
        clearTimeout(debounceTimers[key]);
      }
      debounceTimers[key] = setTimeout(function() {
        delete debounceTimers[key];
        fn();
      }, delay || CONFIG.debounceMs);
    }

    function cartFingerprint(items) {
      return items.map(function(i) { return i.variant_id + ':' + i.quantity; }).sort().join('|');
    }

    function handleCartOpen() {
      if (state.productsCache && state.productsCache.length > 0 && state.visible) {
        return;
      }
      load();
    }

    function handleCartRendered(e) {
      if (e && e.detail) {
        debounce('cartUpdate', updateProductStates, 200);
      }
    }

    function handleCartUpdated(e) {
      if (e && e.detail && e.detail.cart) {
        state.cartItems = e.detail.cart.items || [];
        var fp = cartFingerprint(state.cartItems);

        if (fp === lastCartFingerprint) {
          updateProductStates();
          return;
        }
        lastCartFingerprint = fp;
        clearSessionCache();
        state.productsCache = null;
        updateProductStates();

        if (state.cartItems.length > 0) {
          debounce('cartReload', function() {
            load();
          }, 300);
        } else {
          hide();
        }
      }
    }

    function cancelPendingRequest() {
      if (abortController) {
        abortController.abort();
        abortController = null;
      }
    }

    function safeFetch(url, options) {
      cancelPendingRequest();
      abortController = new AbortController();

      var timeoutId = setTimeout(function() {
        if (abortController) {
          abortController.abort();
        }
      }, CONFIG.apiTimeoutMs);

      return fetch(url, Object.assign({}, options, {
        signal: abortController.signal
      })).then(function(response) {
        clearTimeout(timeoutId);
        return response;
      }).catch(function(error) {
        clearTimeout(timeoutId);
        if (error.name === 'AbortError') {
          return Promise.reject(new Error('Request cancelled or timed out'));
        }
        throw error;
      });
    }

    function safeJsonParse(response) {
      return response.text().then(function(text) {
        if (!text) return null;
        try {
          return JSON.parse(text);
        } catch (e) {
          return null;
        }
      });
    }

    function formatMoney(cents) {
      if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }

      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      var amount = (parseInt(cents, 10) / 100).toFixed(2);
      var currencySymbol = window.theme && window.theme.settings && window.theme.settings.currencySymbol ? window.theme.settings.currencySymbol : '$';
      return currencySymbol + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function getSmartImageUrl(url, targetHeight) {
      if (!url) return '';

      if (url.indexOf('//') === 0) {
        url = 'https:' + url;
      }

      var height = targetHeight || CONFIG.imageHeight;
      var retinaHeight = height * 2;

      if (url.indexOf('cdn.shopify.com') !== -1) {
        return url.replace(/(_\d+x\d+(_crop_\w+)?|_x\d+|_\d+x)(\.jpg|\.jpeg|\.png|\.gif|\.webp)/i, '_x' + retinaHeight + '$3');
      }

      return url;
    }

    function filterByVendor(products) {
      return products.filter(function(p) { return p.vendor !== 'TRLab Shop'; });
    }

    function fetchProducts(contextProductId) {
      var fetchLimit = CONFIG.productsLimit * 2;

      if (CONFIG.upsellCollectionHandle) {
        return fetchCollectionProducts(CONFIG.upsellCollectionHandle, fetchLimit)
          .then(function(products) {
            return padWithFallback(products, fetchLimit);
          });
      }

      var productIds = [];
      if (contextProductId) {
        productIds.push(contextProductId);
      }
      if (state.cartItems.length > 0) {
        state.cartItems.forEach(function(item) {
          if (productIds.indexOf(item.product_id) === -1) {
            productIds.push(item.product_id);
          }
        });
      }

      if (productIds.length === 0) {
        return fetchFallbackProducts(fetchLimit);
      }

      var recommendPromises = productIds.slice(0, 3).map(function(pid) {
        return fetchRecommendedProducts(pid, fetchLimit).then(function(products) {
          return filterByVendor(products);
        });
      });

      return Promise.all(recommendPromises).then(function(allResults) {
        var seen = {};
        var merged = [];
        allResults.forEach(function(products) {
          products.forEach(function(p) {
            if (!seen[p.id]) {
              seen[p.id] = true;
              merged.push(p);
            }
          });
        });

        if (merged.length >= fetchLimit) {
          return merged.slice(0, fetchLimit);
        }
        return padWithFallback(merged, fetchLimit);
      });
    }

    function padWithFallback(products, fetchLimit) {
      var filtered = filterByVendor(products);
      if (filtered.length >= fetchLimit) {
        return filtered.slice(0, fetchLimit);
      }
      return fetchFallbackProducts(fetchLimit).then(function(fallbackProducts) {
        var existingIds = filtered.map(function(p) { return p.id; });
        var additional = filterByVendor(fallbackProducts).filter(function(p) {
          return existingIds.indexOf(p.id) === -1;
        });
        var combined = filtered.concat(additional);
        return combined.slice(0, fetchLimit);
      });
    }

    function fetchRecommendedProducts(productId, limit) {
      var fetchLimit = limit || CONFIG.productsLimit;
      var url = '/recommendations/products.json?product_id=' + productId + '&limit=' + fetchLimit;
      return safeFetch(url, { credentials: 'same-origin' })
        .then(safeJsonParse)
        .then(function(data) { return (data && data.products) || []; })
        .catch(function() { return []; });
    }

    function fetchCollectionProducts(handle, limit) {
      var fetchLimit = limit || CONFIG.productsLimit;
      var url = '/collections/' + handle + '.json?limit=' + fetchLimit;
      return safeFetch(url, { credentials: 'same-origin' })
        .then(safeJsonParse)
        .then(function(data) { return (data && data.collection && data.collection.products) || []; })
        .catch(function() { return []; });
    }

    function fetchFallbackProducts(limit) {
      var fetchLimit = limit || CONFIG.productsLimit * 3;
      var url = '/collections/' + CONFIG.fallbackCollectionHandle + '.json?limit=' + fetchLimit;
      return safeFetch(url, { credentials: 'same-origin' })
        .then(safeJsonParse)
        .then(function(data) { return (data && data.collection && data.collection.products) || []; })
        .catch(function() { return []; });
    }

    function load() {
      if (state.isLoading) return;

      state.isLoading = true;
      showLoading();

      sharedLoad().then(function(products) {
        state.products = products || [];
        if (products && products.length > 0) {
          state.productsCache = products;
        }
        state.isLoading = false;
        render();
      }).catch(function() {
        state.isLoading = false;
        if (!showFallbackProducts()) {
          if (state.productsCache && state.productsCache.length > 0) {
            state.products = state.productsCache;
            render();
          } else {
            showError();
          }
        }
      });
    }

    function refresh() {
      fetch('/cart.js', { credentials: 'same-origin' })
        .then(function(res) { return res.json(); })
        .then(function(cart) {
          state.cartItems = (cart && cart.items) || [];
          updateProductStates();
        })
        .catch(function() {});
    }

    function prefetch(productId) {
      if (!productId && state.cartItems.length > 0) {
        productId = state.cartItems[0].product_id;
      }
      if (!productId) return;

      fetchProducts(productId).then(function(products) {
        if (products && products.length > 0) {
          state.productsCache = products;
        }
      });
    }

    function filterProducts(products) {
      var cartVariantIds = state.cartItems.map(function(item) { return item.variant_id; });
      var cartProductIds = state.cartItems.map(function(item) { return item.product_id; });

      return products.filter(function(product) {
        if (product.vendor === 'TRLab Shop') return false;
        if (cartProductIds.indexOf(product.id) !== -1) {
          var hasAvailableVariant = product.variants && product.variants.some(function(variant) {
            return cartVariantIds.indexOf(variant.id) === -1 && variant.available !== false;
          });
          if (!hasAvailableVariant) return false;
        }
        return product.available !== false;
      });
    }

    function showLoading() {
      if (!domCache.slider) return;

      var filteredProducts = filterProducts(state.products);
      if (filteredProducts.length === 0) {
        var loadingHTML = '<div class="upsell-recommendations__loading">';
        for (var i = 0; i < 2; i++) {
          loadingHTML += '<div class="upsell-skeleton">' +
            '<div class="upsell-skeleton__image"></div>' +
            '<div class="upsell-skeleton__content">' +
            '<div class="upsell-skeleton__title"></div>' +
            '<div class="upsell-skeleton__price"></div>' +
            '<div class="upsell-skeleton__button"></div>' +
            '</div></div>';
        }
        loadingHTML += '</div>';
        domCache.slider.innerHTML = loadingHTML;
      }
      show();
    }

    function showFallbackProducts() {
      if (!domCache.fallbackContainer) return false;

      var fallbackProducts = domCache.fallbackContainer.querySelectorAll('.upsell-product[data-fallback]');
      if (fallbackProducts.length === 0) return false;

      domCache.slider.innerHTML = '';
      fallbackProducts.forEach(function(el) {
        domCache.slider.appendChild(el.cloneNode(true));
      });

      createNavigation(domCache.slider.querySelectorAll('.upsell-product').length);
      initDragCursor();
      show();
      state.visible = true;
      return true;
    }

    function render() {
      if (!domCache.slider) return;

      var filteredProducts = filterProducts(state.products);

      if (filteredProducts.length < CONFIG.productsLimit) {
        var existingIds = filteredProducts.map(function(p) { return p.id; });
        fetchFallbackProducts(CONFIG.productsLimit * 2).then(function(fallbackProducts) {
          var additionalProducts = filterProducts(fallbackProducts).filter(function(p) {
            return existingIds.indexOf(p.id) === -1;
          });
          var combined = filteredProducts.concat(additionalProducts);
          renderProducts(combined.slice(0, CONFIG.productsLimit));
        });
        return;
      }

      renderProducts(filteredProducts.slice(0, CONFIG.productsLimit));
    }

    function renderProducts(products) {
      if (!domCache.slider || !products || products.length === 0) {
        hide();
        return;
      }

      var scrollPos = domCache.slider.scrollLeft;

      var fragment = document.createDocumentFragment();
      products.forEach(function(product) {
        var element = createProductElement(product);
        if (element) {
          fragment.appendChild(element);
        }
      });

      domCache.slider.textContent = '';
      domCache.slider.appendChild(fragment);

      domCache.slider.scrollLeft = scrollPos;

      createNavigation(products.length);
      initDragCursor();
      show();
    }

    function createProductElement(product) {
      if (!domCache.productTemplate) {
        var fallback = document.createElement('div');
        fallback.className = 'upsell-product';
        fallback.setAttribute('data-product-id', product.id);
        fallback.innerHTML = '<a href="/products/' + product.handle + '" class="upsell-product__title">' + (product.title || '') + '</a>';
        return fallback;
      }

      var clone = domCache.productTemplate.content.cloneNode(true);
      var element = clone.querySelector('.upsell-product');
      if (!element) return null;

      var imageEl = element.querySelector('.upsell-product__image');
      var imageLink = element.querySelector('.upsell-product__image-link');
      var titleEl = element.querySelector('.upsell-product__title');
      var priceEl = element.querySelector('.upsell-product__price');
      var addBtn = element.querySelector('.upsell-product__add-btn');
      var variantSelectWrapper = element.querySelector('.upsell-product__variant-select-wrapper');
      var variantSelect = element.querySelector('.upsell-product__variant-select');

      var imageUrl = getSmartImageUrl(product.featured_image || (product.images && product.images[0]) || '', CONFIG.imageHeight);

      if (imageEl) {
        imageEl.loading = 'lazy';
        imageEl.decoding = 'async';
        imageEl.src = imageUrl;
        imageEl.alt = product.title || '';
        imageEl.style.height = '100%';
        imageEl.style.width = 'auto';
        imageEl.style.maxWidth = '100%';
        imageEl.style.objectFit = 'contain';
      }
      if (imageLink) {
        imageLink.href = product.url || '/products/' + product.handle;
      }
      if (titleEl) {
        titleEl.textContent = product.title || '';
        titleEl.href = product.url || '/products/' + product.handle;
      }

      var price = product.price;
      var comparePrice = product.compare_at_price || product.compare_at_price_max;

      if (priceEl) {
        if (comparePrice && comparePrice > price) {
          priceEl.innerHTML = '<span class="upsell-product__price--sale">' + formatMoney(price) + '</span> <span class="upsell-product__price--compare">' + formatMoney(comparePrice) + '</span>';
        } else {
          priceEl.textContent = formatMoney(price);
        }
      }

      var variants = product.variants || [];
      var availableVariants = variants.filter(function(v) { return v.available !== false; });

      if (variants.length > 1 && availableVariants.length > 0) {
        if (variantSelectWrapper && variantSelect) {
          variantSelectWrapper.style.display = 'block';

          availableVariants.forEach(function(variant) {
            var option = document.createElement('option');
            option.value = variant.id;
            option.textContent = variant.title || 'Default';
            if (!variant.available) {
              option.disabled = true;
              option.textContent += ' (Sold Out)';
            }
            variantSelect.appendChild(option);
          });

          var firstAvailable = availableVariants.find(function(v) { return v.available !== false; }) || availableVariants[0];
          if (firstAvailable) {
            variantSelect.value = firstAvailable.id;
            addBtn.setAttribute('data-variant-id', firstAvailable.id);
          }
        }
      } else if (variants.length === 1) {
        addBtn.setAttribute('data-variant-id', variants[0].id);
      }

      element.setAttribute('data-product-id', product.id);
      productDataMap.set(product.id, product);

      var cartVariantIds = state.cartItems.map(function(item) { return item.variant_id; });
      var isAdded = variants.some(function(v) { return cartVariantIds.indexOf(v.id) !== -1; });

      if (isAdded) {
        markAsAdded(element);
      }

      return element;
    }

    function handleVariantChange(selectEl) {
      var productEl = selectEl.closest('.upsell-product');
      var addBtn = productEl.querySelector('.upsell-product__add-btn');
      var imageEl = productEl.querySelector('.upsell-product__image');
      var priceEl = productEl.querySelector('.upsell-product__price');

      var variantId = selectEl.value;
      if (addBtn) {
        addBtn.setAttribute('data-variant-id', variantId);
      }

      var productId = parseInt(productEl.dataset.productId, 10);
      var productData = productDataMap.get(productId);
      if (!productData) return;

      var selectedVariant = productData.variants.find(function(v) { return String(v.id) === String(variantId); });
      if (!selectedVariant) return;

      if (priceEl) {
        var price = selectedVariant.price;
        var comparePrice = selectedVariant.compare_at_price;
        if (comparePrice && comparePrice > price) {
          priceEl.innerHTML = '<span class="upsell-product__price--sale">' + formatMoney(price) + '</span> <span class="upsell-product__price--compare">' + formatMoney(comparePrice) + '</span>';
        } else {
          priceEl.textContent = formatMoney(price);
        }
      }

      if (imageEl && selectedVariant.featured_image) {
        var variantImage = selectedVariant.featured_image;
        if (typeof variantImage === 'object' && variantImage.src) {
          variantImage = variantImage.src;
        }
        if (typeof variantImage === 'string') {
          var smartUrl = getSmartImageUrl(variantImage, CONFIG.imageHeight);
          requestAnimationFrame(function() {
            imageEl.src = smartUrl;
          });
        }
      }
    }

    function handleAddToCart(btn) {
      var variantId = btn.getAttribute('data-variant-id');
      if (!variantId) return;

      var productEl = btn.closest('.upsell-product');
      var productId = parseInt(productEl.dataset.productId, 10);

      applyOptimisticUpdate(productEl, variantId, productId);

      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      fetch('/cart/add.js', {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      })
        .then(function(response) {
          return response.text().then(function(text) {
            var data = text ? JSON.parse(text) : null;
            if (!response.ok) {
              revertOptimisticUpdate(productEl, variantId);
              throw new Error((data && (data.description || data.message)) || 'Add to cart failed');
            }
            return data;
          });
        })
        .then(function(data) {
          setButtonLoading(btn, false);
          showSuccessToast('Added to cart');

          removeProductWithAnimation(productEl);
          notifyThemeCartUpdated(data);

          prefetchNextProducts(productId);
        })
        .catch(function(error) {
          setButtonLoading(btn, false);
          showErrorToast(error.message || 'Failed to add');
        });
    }

    function applyOptimisticUpdate(productEl, variantId, productId) {
      state.cartItems.push({
        variant_id: parseInt(variantId, 10),
        product_id: productId,
        quantity: 1
      });

      markAsAdded(productEl);
      setButtonLoading(productEl.querySelector('.upsell-product__add-btn'), true);
    }

    function revertOptimisticUpdate(productEl, variantId) {
      state.cartItems = state.cartItems.filter(function(item) {
        return item.variant_id !== parseInt(variantId, 10);
      });

      var addBtn = productEl.querySelector('.upsell-product__add-btn');
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.classList.remove('is-added');
        var textEl = addBtn.querySelector('.upsell-product__add-btn-text');
        if (textEl) {
          textEl.textContent = 'Add to cart';
        }
      }
      productEl.classList.remove('upsell-product--in-cart');
    }

    function prefetchNextProducts(currentProductId) {
      var nextProductId = null;

      if (state.cartItems.length > 0) {
        for (var i = state.cartItems.length - 1; i >= 0; i--) {
          if (state.cartItems[i].product_id !== currentProductId) {
            nextProductId = state.cartItems[i].product_id;
            break;
          }
        }
      }

      if (nextProductId) {
        fetchProducts(nextProductId).then(function(products) {
          if (products && products.length > 0) {
            state.productsCache = products;
          }
        });
      }
    }

    function notifyThemeCartUpdated(data) {
      document.dispatchEvent(new CustomEvent('upsell:product:added', {
        bubbles: true,
        detail: Object.assign({}, data || {}, { _upsellInternal: true, quantity: 1 })
      }));

      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        bubbles: true,
        detail: { _upsellInternal: true }
      }));

      if (document.body.classList.contains('template-cart')) {
        debounce('cartBuild', function() {
          document.dispatchEvent(new CustomEvent('cart:build'));
        }, 300);
      }
    }

    function removeProductWithAnimation(productEl) {
      if (!productEl || !productEl.parentNode) return;

      productEl.classList.add('is-removing');

      setTimeout(function() {
        if (productEl.parentNode) {
          productEl.parentNode.removeChild(productEl);
        }
        if (!domCache.slider) return;
        updateNavigation();

        var remainingProducts = domCache.slider.querySelectorAll('.upsell-product');
        if (remainingProducts.length === 0) {
          load();
        }
      }, 300);
    }

    function updateProductStates() {
      if (!domCache.slider) return;

      var cartVariantIds = state.cartItems.map(function(item) { return item.variant_id; });

      var productElements = domCache.slider.querySelectorAll('.upsell-product');
      var removeIndex = 0;

      productElements.forEach(function(el) {
        var variantSelect = el.querySelector('.upsell-product__variant-select');
        var addBtn = el.querySelector('.upsell-product__add-btn');
        var isInCart = false;

        if (variantSelect) {
          isInCart = cartVariantIds.indexOf(parseInt(variantSelect.value, 10)) !== -1;
        } else if (addBtn) {
          isInCart = cartVariantIds.indexOf(parseInt(addBtn.getAttribute('data-variant-id'), 10)) !== -1;
        }

        if (isInCart) {
          setTimeout(function() {
            removeProductWithAnimation(el);
          }, removeIndex * 150);
          removeIndex++;
        }
      });
    }

    function createNavigation(count) {
      var existingNav = domCache.container.querySelector('.upsell-recommendations__nav');
      if (existingNav) {
        existingNav.parentNode.removeChild(existingNav);
      }

      if (count <= 1) return;

      var nav = document.createElement('div');
      nav.className = 'upsell-recommendations__nav';

      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.className = 'upsell-recommendations__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', 'Go to product ' + (i + 1));
        dot.setAttribute('data-index', i);
        nav.appendChild(dot);
      }

      domCache.slider.parentNode.appendChild(nav);

      domCache.slider.removeEventListener('scroll', handleScroll);
      domCache.slider.addEventListener('scroll', handleScroll);
    }

    function handleScroll() {
      var scrollLeft = domCache.slider.scrollLeft;
      var containerWidth = domCache.slider.offsetWidth;
      var activeIndex = Math.round(scrollLeft / containerWidth);

      var dots = domCache.container.querySelectorAll('.upsell-recommendations__dot');
      dots.forEach(function(dot, i) {
        dot.classList.toggle('is-active', i === activeIndex);
      });
    }

    function scrollToProduct(index) {
      var products = domCache.slider.querySelectorAll('.upsell-product');
      if (products[index]) {
        products[index].scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    }

    function updateNavigation() {
      if (!domCache.slider) return;
      var remainingProducts = domCache.slider.querySelectorAll('.upsell-product');
      createNavigation(remainingProducts.length);
    }

    function initDragCursor() {
      if (domCache.slider) {
        domCache.slider.style.cursor = 'grab';
      }
    }

    function handleDragStart(e) {
      if (e.target.tagName === 'SELECT' || e.target.tagName === 'BUTTON' || e.target.tagName === 'A') {
        return;
      }
      dragState.isDragging = true;
      dragState.moved = false;
      dragState.startX = e.pageX - domCache.slider.offsetLeft;
      dragState.scrollLeft = domCache.slider.scrollLeft;
      domCache.slider.style.cursor = 'grabbing';
      domCache.slider.style.userSelect = 'none';
    }

    function handleDragMove(e) {
      if (!dragState.isDragging) return;
      e.preventDefault();
      var x = e.pageX - domCache.slider.offsetLeft;
      var walk = (x - dragState.startX) * 1.5;
      if (Math.abs(walk) > 5) {
        dragState.moved = true;
      }
      domCache.slider.scrollLeft = dragState.scrollLeft - walk;
    }

    function handleDragEnd() {
      if (!dragState.isDragging) return;
      dragState.isDragging = false;
      domCache.slider.style.cursor = 'grab';
      domCache.slider.style.userSelect = '';
      setTimeout(function() {
        dragState.moved = false;
      }, 10);
    }

    function handleTouchStart(e) {
      dragState.startX = e.touches[0].pageX - domCache.slider.offsetLeft;
      dragState.moved = false;
    }

    function handleTouchMove(e) {
      var x = e.touches[0].pageX - domCache.slider.offsetLeft;
      if (Math.abs(x - dragState.startX) > 10) {
        dragState.moved = true;
      }
    }

    function handleTouchEnd() {
      setTimeout(function() {
        dragState.moved = false;
      }, 100);
    }

    function setButtonLoading(btn, isLoading) {
      if (!btn) return;
      var textEl = btn.querySelector('.upsell-product__add-btn-text');
      var loadingEl = btn.querySelector('.upsell-product__add-btn-loading');

      if (isLoading) {
        btn.disabled = true;
        btn.classList.add('is-loading');
        if (textEl) textEl.style.display = 'none';
        if (loadingEl) loadingEl.style.display = 'inline-flex';
      } else {
        btn.disabled = false;
        btn.classList.remove('is-loading');
        if (textEl) textEl.style.display = 'inline';
        if (loadingEl) loadingEl.style.display = 'none';
      }
    }

    function markAsAdded(element) {
      element.classList.add('upsell-product--in-cart');
      var addBtn = element.querySelector('.upsell-product__add-btn');
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.classList.add('is-added');
        var textEl = addBtn.querySelector('.upsell-product__add-btn-text');
        if (textEl) {
          textEl.textContent = 'Added';
        }
      }
    }

    function showToast(options) {
      var type = options.type || 'info';
      var message = options.message || '';
      var duration = options.duration || 3000;
      var icon = options.icon || '';

      var existingToast = document.querySelector('.upsell-toast');
      if (existingToast) existingToast.parentNode.removeChild(existingToast);

      var toast = document.createElement('div');
      toast.className = 'upsell-toast upsell-toast--' + type;

      var content = '';
      if (icon) {
        content += icon;
      }
      content += message;
      toast.innerHTML = content;

      document.body.appendChild(toast);

      requestAnimationFrame(function() {
        toast.classList.add('is-visible');
      });

      setTimeout(function() {
        toast.classList.remove('is-visible');
        setTimeout(function() {
          if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, 300);
      }, duration);
    }

    function showSuccessToast(message) {
      var successIcon = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="margin-right: 8px;"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      showToast({
        type: 'success',
        message: message || 'Added to cart',
        icon: successIcon,
        duration: 2000
      });
    }

    function showErrorToast(message) {
      showToast({
        type: 'error',
        message: message || 'Failed to add',
        duration: 3000
      });
    }

    function show() {
      if (domCache.container) {
        domCache.container.classList.remove('is-hidden');
        state.visible = true;
      }
    }

    function hide() {
      if (domCache.container) {
        domCache.container.classList.add('is-hidden');
        state.visible = false;
      }
    }

    function showError() {
      if (domCache.slider) {
        domCache.slider.innerHTML = '<div class="upsell-recommendations__error">Unable to load recommendations</div>';
        show();
      }
    }

    function destroy() {
      cancelPendingRequest();
      _loadPromise = null;

      Object.keys(debounceTimers).forEach(function(key) {
        clearTimeout(debounceTimers[key]);
      });
      debounceTimers = {};

      if (state.initialized) {
        unbindEvents();
      }

      productDataMap.clear();
      boundHandlers = {};

      state = {
        cartItems: [],
        products: [],
        productsCache: null,
        isLoading: false,
        initialized: false,
        visible: false
      };

      domCache = {};
      instance = null;
    }

    return getInstance();
  })();

  function initUpsell() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', UpsellRecommendations.init);
    } else {
      UpsellRecommendations.init();
    }
  }

  initUpsell();

  window.UpsellRecommendations = UpsellRecommendations;
})();
